<template>
    <div class="aside-box" :class="{ 'is-collapse': isCollapse, 'is-hover-expanded': isHoverExpanded }">
        <div class="menu-wrap" :class="{ 'menu-wrap-hover': isHoverExpanded }">
            <ElScrollbar noresize @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
                <ElMenu
                    v-if="menuList.length"
                    :unique-opened="false"
                    :default-active="menuActive"
                    :default-openeds="defaultOpeneds"
                    :collapse="menuActualCollapse"
                    :collapse-transition="false"
                    class="zh-menu"
                    popper-effect="light"
                >
                    <template v-for="subItem in menuList" :key="subItem.name">
                        <MenuItem :item="subItem" />
                    </template>
                </ElMenu>
            </ElScrollbar>
            <div class="collapse-button" @click="changeCollapse">
                <i v-if="!isCollapse" class="iconfont icon-a-putaway1"></i>
                <i v-else class="iconfont icon-expand"></i>
                <span v-if="!isCollapse || isHoverExpanded" class="collapse-button-text">{{
                    isCollapse ? "展开导航" : "收起导航"
                }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMenu, ElScrollbar } from "element-plus";
import { useMenu } from "./useMenu";
import MenuItem from "./menuItem.vue";
import { useGlobalStore } from "@/stores/global";

const router = useRouter();
const globalStore = useGlobalStore();

const isCollapse = computed<boolean>(() => globalStore.isCollapse);
// 鼠标移入展开的状态（不影响 isCollapse）
const isHoverExpanded = ref<boolean>(false);
const { menuList } = useMenu();
const defaultOpeneds = computed(
    () => ["PurchaseSalesInventory", "WarehouseSupervision", router.currentRoute.value.matched[1]?.name] as string[]
);
const menuActive = computed<string>(() => {
    return (router.currentRoute.value.meta.activeMenu as string) || (router.currentRoute.value.name as string);
});

// 实际折叠状态 = 全局折叠状态 && 非鼠标移入展开状态
const menuActualCollapse = computed(() => {
    return isCollapse.value && !isHoverExpanded.value;
});

// 收起/展开 导航
const changeCollapse = () => {
    // 清除鼠标移入展开的标记
    isHoverExpanded.value = false;
    // 切换全局折叠状态
    globalStore.setIsCollapse(!globalStore.isCollapse);
};
// 鼠标移入菜单区域时的处理
const handleMouseEnter = () => {
    // 如果菜单处于折叠状态，通过鼠标移入展开（不改变 isCollapse）
    if (isCollapse.value) {
        isHoverExpanded.value = true;
    }
};

// 鼠标移出菜单区域时的处理
const handleMouseLeave = () => {
    // 恢复折叠状态（不改变 isCollapse）
    // 当鼠标移出 el-scrollbar 区域（包括移到折叠按钮上）时，恢复折叠
    if (isHoverExpanded.value) {
        isHoverExpanded.value = false;
    }
};
</script>

<style lang="scss" scoped>
.aside-box {
    position: relative;
    height: 100%;
    width: var(--el-aside-width);
    //transition: width 0.3s linear 0s;
    // 折叠状态：宽度为 50px，即使鼠标移入也保持 50px 不变
    &.is-collapse {
        width: var(--zh-aside-width-collapse);
        // 折叠时宽度延迟变化
        //transition: width 0.3s linear 0.1s;
    }
    // 鼠标移入菜单展开状态：宽度为 50px,不影响右侧内容
    &.is-hover-expanded {
        width: var(--zh-aside-width-collapse);
    }
    .menu-wrap {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: #fff;
        box-sizing: border-box;
        background-image: linear-gradient(180deg, #2f4562 0%, #1c385c 100%);
        :deep(.el-scrollbar__bar.is-vertical) {
            right: 0;
            width: 0;
        }
    }
    .collapse-button {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        height: 44px;
        padding: 6px 17px;
        gap: 10px;
        cursor: pointer;
        box-sizing: border-box;
        margin-bottom: 8px;
        color: #fff;
        z-index: 1001;
        &:hover {
            background: #3c7cae4d;
        }
    }

    .collapse-button-text {
        font-size: 14px;
    }
}

// 折叠状态下鼠标移入时，菜单展开显示, menu-wrap 悬浮展开
.aside-box.is-collapse .menu-wrap.menu-wrap-hover {
    position: absolute;
    left: 0;
    top: 0;
    width: var(--el-aside-width);
    z-index: 3000;

    // 折叠状态下鼠标移入时菜单展开显示效果
    .zh-menu.el-menu--collapse {
        width: var(--el-aside-width);
        overflow: auto;
        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
            span {
                opacity: 1;
                max-width: 200px;
            }
            .menu-icon {
                margin-right: 10px;
            }
        }
        :deep(.el-sub-menu__icon-arrow) {
            display: block !important;
        }
    }
}
.zh-menu {
    height: 100%;
    &.el-menu {
        position: relative;
        border-right: none;
        width: 100%;
        height: 100%;
        padding: 0 6px;
        :deep(.layout-menu-wrap) {
            padding-bottom: 3px;
            padding-top: 3px;
        }
        :deep(.el-menu-item) {
            box-sizing: border-box;
            border-radius: var(--el-menu-border-radius);
            border-left: 2px solid transparent;
            &.is-active {
                background: #fff !important;
                font-weight: 500;
                // border-color: #2143fc;
            }
            .el-sub-menu__icon-arrow {
                transform: rotateZ(270deg) !important;
            }
        }
        :deep(.el-sub-menu) {
            border-left: 2px solid transparent;
            .el-sub-menu__title:hover {
                border-radius: var(--el-menu-border-radius);
            }
        }
    }

    // 菜单项和子菜单标题中的文字
    :deep(.el-menu-item),
    :deep(.el-sub-menu__title) {
        span {
            // 展开状态：文字延迟显示（宽度先变化，然后文字再显示）
            transition:
                opacity 0.2s linear 0.1s,
                max-width 0.2s linear 0.1s;
            opacity: 1;
            max-width: 200px;
            overflow: hidden;
            white-space: nowrap;
        }
    }

    // 折叠状态下，菜单宽度为 50px
    &.el-menu--collapse {
        width: var(--zh-aside-width-collapse);
        overflow: hidden;
        :deep(.el-menu-item) {
            span {
                opacity: 0;
                max-width: 0;
                // 文字隐藏立即开始，不延迟
                transition:
                    opacity 0.2s linear 0s,
                    max-width 0.2s linear 0s;
            }
        }
        // 子菜单标题
        :deep(.el-sub-menu > .el-sub-menu__title) {
            span {
                opacity: 0;
                max-width: 0;
                // 文字隐藏立即开始，不延迟
                transition:
                    opacity 0.2s linear 0s,
                    max-width 0.2s linear 0s;
            }
            .el-sub-menu__icon-arrow {
                display: none !important;
            }
            .menu-icon {
                width: 16px;
                margin-right: 0;
            }
        }
        :deep(.el-menu-item.is-active),
        :deep(.el-sub-menu.is-active > .el-sub-menu__title) {
            border-radius: 6px;
            background: #fff;
        }
    }
}

.aside-box.is-collapse .menu-wrap.menu-wrap-hover .collapse-button {
    &:hover {
        border-radius: var(--el-menu-border-radius);
    }
}
</style>
